import { parseAmount } from "./convertNumbers";

const STABLE_SYMBOLS = new Set(["USDT", "USDC"]);

export const STABLE_DUST_MIN = 0.001;

const FIAT_CURRENCIES = new Set([
  "ZAR",
  "USD",
  "EUR",
  "GBP",
  "JPY",
  "AUD",
  "CAD",
  "CHF",
  "CNY",
]);

interface SellAssetsResult {
  outgoingAsset: string;
  incomingAsset: string;
}

interface MongoDecimal {
  $numberDecimal?: string | number;
}

type DecimalField = MongoDecimal | string | number | undefined;

interface Transaction {
  transactionType?: string;
  transactionSubtype?: string;
  asset?: string;
  currency?: string;
  cryptoAsset?: string;
  amount?: DecimalField;
  transactionRef?: {
    incomingAsset?: string;
    outgoingAsset?: string;
    incomingAmount?: DecimalField;
    outgoingAmount?: DecimalField;
    price?: DecimalField;
    total?: DecimalField;
    totalAmount?: DecimalField;
    amount?: DecimalField;
    [key: string]: unknown;
  };
  [key: string]: unknown;
}

/** Extracts the $numberDecimal string from a Mongo Decimal128 field, or returns the raw value if it's already a primitive. */
function decimal(field: DecimalField): string | number | undefined {
  if (field === null || field === undefined) return undefined;
  if (typeof field === "object" && "$numberDecimal" in field) {
    return field.$numberDecimal;
  }
  return field as string | number;
}

export function isFiatCurrency(asset: string | null | undefined): boolean {
  if (!asset) return false;
  return FIAT_CURRENCIES.has(asset.toUpperCase());
}

export function determineSellAssets(transaction: {
  asset?: string;
  currency?: string;
  [key: string]: unknown;
}): SellAssetsResult {
  const asset = transaction?.asset?.toUpperCase();
  const currency = transaction?.currency?.toUpperCase();

  if (asset && currency) {
    const assetIsFiat = isFiatCurrency(asset);
    const currencyIsFiat = isFiatCurrency(currency);

    if (assetIsFiat && !currencyIsFiat) {
      return { outgoingAsset: currency, incomingAsset: asset };
    }
    if (!assetIsFiat && currencyIsFiat) {
      return { outgoingAsset: asset, incomingAsset: currency };
    }
    if (!assetIsFiat && !currencyIsFiat) {
      return { outgoingAsset: asset, incomingAsset: currency || "ZAR" };
    }
    return {
      outgoingAsset: asset || currency,
      incomingAsset: currency || asset || "ZAR",
    };
  }

  return {
    outgoingAsset: asset || currency || "",
    incomingAsset: currency || asset || "ZAR",
  };
}

function resolveUserTradeLegAssets(
  tx: Transaction,
  subtype: string
): { incoming: string; outgoing: string } {
  let incoming = tx.transactionRef?.incomingAsset?.toUpperCase() || "";
  let outgoing = tx.transactionRef?.outgoingAsset?.toUpperCase() || "";

  if (!incoming || !outgoing) {
    if (subtype === "buy") {
      incoming =
        incoming || tx.asset?.toUpperCase() || tx.currency?.toUpperCase() || "";
      outgoing =
        outgoing || tx.currency?.toUpperCase() || tx.asset?.toUpperCase() || "";
    } else if (subtype === "sell") {
      const sellAssets = determineSellAssets(tx);
      incoming = incoming || sellAssets.incomingAsset;
      outgoing = outgoing || sellAssets.outgoingAsset;
    }
  }

  return { incoming, outgoing };
}

function userTradeBuyStableLegValue(tx: Transaction): number {
  const r = tx.transactionRef;
  const cryptoAmount = parseAmount(
    decimal(r?.incomingAmount) || decimal(r?.total) || decimal(r?.totalAmount)
  );
  const price = parseAmount(decimal(r?.price) || 0);
  if (price > 0 && cryptoAmount > 0) {
    return cryptoAmount * price;
  }
  return parseAmount(
    decimal(r?.outgoingAmount) ||
      decimal(r?.total) ||
      decimal(r?.totalAmount) ||
      decimal(r?.amount) ||
      0
  );
}

function userTradeSellStableIncomingValue(tx: Transaction): number {
  const r = tx.transactionRef;
  return parseAmount(
    decimal(r?.incomingAmount) ||
      decimal(r?.total) ||
      decimal(r?.totalAmount) ||
      decimal(r?.amount) ||
      0
  );
}

function nonTradeStableAmount(tx: Transaction): number {
  const r = tx.transactionRef;
  return parseAmount(
    decimal(r?.amount) ||
      decimal(r?.totalAmount) ||
      decimal(r?.total) ||
      decimal(tx.amount) ||
      0
  );
}

export function isStablecoinDustTx(tx: Transaction): boolean {
  if (tx.transactionType === "UserTrade") {
    const subtype = tx.transactionSubtype?.toLowerCase() || "";
    const { incoming, outgoing } = resolveUserTradeLegAssets(tx, subtype);

    if (subtype === "buy" && STABLE_SYMBOLS.has(outgoing)) {
      return userTradeBuyStableLegValue(tx) < STABLE_DUST_MIN;
    }
    if (subtype === "sell" && STABLE_SYMBOLS.has(incoming)) {
      return userTradeSellStableIncomingValue(tx) < STABLE_DUST_MIN;
    }
    return false;
  }

  const assetStr =
    typeof tx.asset === "string"
      ? tx.asset
      : typeof tx.cryptoAsset === "string"
        ? tx.cryptoAsset
        : typeof tx.currency === "string"
          ? tx.currency
          : "";
  const asset = assetStr.toUpperCase();
  if (!STABLE_SYMBOLS.has(asset)) {
    return false;
  }

  return nonTradeStableAmount(tx) < STABLE_DUST_MIN;
}

export function withoutStablecoinDust<T extends Record<string, unknown>>(
  transactions: T[] | null | undefined
): T[] {
  const list = transactions || [];
  return list.filter((tx) => !isStablecoinDustTx(tx as Transaction));
}
